import { useEffect, useState } from 'preact/hooks';
import { dataHora } from '../admin/comum';
import { api, mensagemDoErro } from '../api';
import { fazerLogout } from '../session';
import { modoOffline } from '../state';
import type { Usuario } from '../types';
import { Avisos, Barra, Carregando, DialogoConfirmar, Selo, avisar, confirmar } from '../ui';
import { BotaoInstalar } from '../componentes/Instalar';
import { BotaoTema } from '../componentes/Tema';
import { cacheGravar, cacheLer } from './armazem';
import { Formulario, type PesquisaAberta } from './Formulario';
import { pendentesDoMunicipio, type Canal } from './regras';
import { avisoEnvio, carregarFila, descartar, enviarPendentes, fila, iniciarSincronizacao, online, pendentes, rejeitadas, sincronizando, tentarDeNovo, ultimoEnvio } from './sync';

type Tela = { nome: 'inicio' } | { nome: 'form'; pesquisaId: string; codIbge: string } | { nome: 'fila' };

const dia = (iso: string) => new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

/** App de coleta do pesquisador: celular, botões grandes e funcionamento sem internet (as respostas ficam no aparelho até a conexão voltar). */
export function ColetaApp({ u }: { u: Usuario }) {
  const [tela, setTela] = useState<Tela>({ nome: 'inicio' });
  const [abertas, setAbertas] = useState<PesquisaAberta[] | null>(null);
  const [doCache, setDoCache] = useState<string | null>(null);
  const [erro, setErro] = useState('');
  const [pronto, setPronto] = useState(false);
  const [canal, setCanal] = useState<Canal | ''>('');
  const offline = !online.value || modoOffline.value;

  async function carregarLista() {
    const chave = `abertas:${u.id}`;
    // Sem sessão de verdade (app aberto sem internet) não se chama a API: usa só a cópia guardada.
    if (!modoOffline.value) {
      try {
        const dados = await api<PesquisaAberta[]>('/pesquisas/abertas');
        setAbertas(dados);
        setDoCache(null);
        setErro('');
        await cacheGravar(chave, dados).catch(() => {});
        return;
      } catch (e) {
        const copia = await cacheLer<PesquisaAberta[]>(chave).catch(() => null);
        if (!copia) return setErro(mensagemDoErro(e));
        setAbertas(copia.dados);
        setDoCache(copia.salvoEm);
        return setErro('');
      }
    }
    const copia = await cacheLer<PesquisaAberta[]>(chave).catch(() => null);
    if (copia) {
      setAbertas(copia.dados);
      setDoCache(copia.salvoEm);
      setErro('');
    } else setErro('Sem internet e sem pesquisas baixadas neste aparelho. Conecte-se uma vez para baixá-las.');
  }

  useEffect(() => {
    let parar = () => {};
    void (async () => {
      try {
        await carregarFila(u.id);
      } catch (e) {
        setErro((e as Error).message);
      }
      setPronto(true);
      parar = iniciarSincronizacao();
    })();
    return () => parar();
  }, [u.id]);

  // Recarrega a lista (e o progresso) ao abrir, quando um envio termina e quando a sessão volta do modo offline.
  useEffect(() => {
    void carregarLista();
  }, [u.id, ultimoEnvio.value, modoOffline.value]);

  async function sair() {
    const n = pendentes().length;
    if (n > 0 && !(await confirmar(`Há ${n} resposta${n === 1 ? '' : 's'} guardada${n === 1 ? '' : 's'} neste aparelho que ainda não ${n === 1 ? 'foi enviada' : 'foram enviadas'}. Elas continuam aqui e serão enviadas quando você entrar de novo com a sua conta. Sair mesmo assim?`, { confirmar: 'Sair', perigo: true }))) return;
    await fazerLogout().catch(() => {});
  }

  const nPend = pendentes().length;
  const nRej = rejeitadas().length;

  return (
    <div class="coleta">
      <header class="coleta-topo">
        <div>
          <strong class="coleta-marca">MAPAS · Coleta</strong>
          <small class="dica">{u.nome}</small>
        </div>
        <div class="coleta-status">
          <span class={`pilula ${offline ? 'pilula-ruim' : 'pilula-ok'}`} role="status">{offline ? 'Sem internet' : 'Conectado'}</span>
          <button type="button" class="botao" onClick={() => setTela({ nome: 'fila' })} aria-label={`Respostas guardadas: ${nPend} para enviar${nRej ? `, ${nRej} recusadas` : ''}`}>
            Fila <span class={`contador${nPend + nRej > 0 ? ' alerta' : ''}`}>{nPend + nRej}</span>
          </button>
          <BotaoInstalar class="botao compacto" rotulo="Instalar" />
          <BotaoTema />
          <button type="button" class="link" onClick={() => void sair()}>Sair</button>
        </div>
      </header>

      <main class="coleta-corpo">
        {nPend > 0 && (
          <p class="aviso" role="status">
            <strong>{nPend} resposta{nPend === 1 ? '' : 's'} guardada{nPend === 1 ? '' : 's'} neste aparelho.</strong>{' '}
            {sincronizando.value ? 'Enviando…' : offline ? 'Serão enviadas quando a internet voltar.' : avisoEnvio.value || 'Aguardando envio.'}
          </p>
        )}
        {nRej > 0 && <p class="erro caixa-erro" role="alert">{nRej} resposta{nRej === 1 ? ' foi recusada' : 's foram recusadas'} pelo servidor. <button type="button" class="link" onClick={() => setTela({ nome: 'fila' })}>Ver motivo</button></p>}

        {tela.nome === 'inicio' && (
          <Inicio abertas={abertas} erro={erro} pronto={pronto} doCache={doCache} onNova={(pesquisaId, codIbge) => setTela({ nome: 'form', pesquisaId, codIbge })} />
        )}

        {tela.nome === 'form' && (() => {
          const p = abertas?.find((x) => x.id === tela.pesquisaId);
          const m = p?.municipios.find((x) => x.codIbge === tela.codIbge);
          if (!p || !m) return <p class="vazio-grande">Esta pesquisa não está mais disponível. <button type="button" class="link" onClick={() => setTela({ nome: 'inicio' })}>Voltar</button></p>;
          return <Formulario key={p.id + m.codIbge} pesquisa={p} municipio={m} usuarioId={u.id} canalInicial={canal} onCanal={setCanal} onVoltar={() => setTela({ nome: 'inicio' })} />;
        })()}

        {tela.nome === 'fila' && <Fila offline={offline} onVoltar={() => setTela({ nome: 'inicio' })} />}
      </main>
      <Avisos />
      <DialogoConfirmar />
    </div>
  );
}

function Inicio({ abertas, erro, pronto, doCache, onNova }: { abertas: PesquisaAberta[] | null; erro: string; pronto: boolean; doCache: string | null; onNova: (p: string, m: string) => void }) {
  if (erro && !abertas) return <p class="erro caixa-erro" role="alert">{erro}</p>;
  if (!abertas || !pronto) return <Carregando texto="Carregando suas pesquisas…" />;
  return (
    <>
      {doCache && <p class="dica">Lista salva em {dataHora(doCache)}: sem conexão com o servidor, o progresso pode estar desatualizado.</p>}
      {abertas.length === 0 && (
        <p class="vazio-grande">Nenhuma pesquisa aberta para você agora. Quando o gestor abrir uma pesquisa nos seus municípios, ela aparece aqui.</p>
      )}
      {abertas.map((p) => (
        <section key={p.id} class="coleta-pesquisa" aria-label={p.titulo}>
          <h2>{p.titulo}</h2>
          <p class="dica">Coleta até {dia(p.periodoFim)}</p>
          <ul class="coleta-municipios">
            {p.municipios.map((m) => {
              const locais = pendentesDoMunicipio(fila.value, p.id, m.codIbge);
              const total = m.coletadasNoMunicipio + locais;
              const pct = m.meta > 0 ? Math.min(100, Math.round((total / m.meta) * 100)) : 0;
              return (
                <li key={m.codIbge}>
                  <div class="coleta-municipio">
                    <strong>{m.nome}</strong>
                    <Barra pct={pct} cor={total >= m.meta ? 'var(--verde)' : undefined} />
                    <small class="dica">
                      {total} de {m.meta} no município · {m.coletadasPorMim + locais} suas{locais > 0 ? ` (${locais} ainda no aparelho)` : ''}
                      {total >= m.meta && <> · <strong>meta atingida</strong></>}
                    </small>
                  </div>
                  <button type="button" class="botao primario" onClick={() => onNova(p.id, m.codIbge)}>Nova resposta</button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </>
  );
}

function Fila({ offline, onVoltar }: { offline: boolean; onVoltar: () => void }) {
  const itens = [...fila.value].sort((a, b) => b.criadaEm.localeCompare(a.criadaEm));
  async function descartarItem(id: string, rotulo: string) {
    if (!(await confirmar(`Descartar a resposta de ${rotulo}? Ela não será enviada e não dá para recuperar.`, { confirmar: 'Descartar', perigo: true }))) return;
    await descartar(id);
    avisar('Resposta descartada.');
  }
  return (
    <section class="coleta-fila" aria-label="Respostas guardadas no aparelho">
      <div class="coleta-form-topo">
        <button type="button" class="botao" onClick={onVoltar}>← Voltar</button>
        <h2>Respostas guardadas</h2>
      </div>
      <p class="dica">Tudo o que você guarda fica neste aparelho até ser enviado. Nada se perde se a internet cair ou o app for fechado.</p>
      <div class="modal-acoes">
        <button type="button" class="botao primario" disabled={offline || pendentes().length === 0 || sincronizando.value} onClick={() => void enviarPendentes()}>
          {sincronizando.value ? 'Enviando…' : 'Enviar agora'}
        </button>
      </div>
      {itens.length === 0 && <p class="vazio-grande">Nada guardado: tudo já foi enviado.</p>}
      <ul class="coleta-itens">
        {itens.map((i) => {
          const rotulo = `${i.municipioNome} (${dataHora(i.criadaEm)})`;
          return (
            <li key={i.idLocal} class={i.estado === 'rejeitada' ? 'recusada' : ''}>
              <div>
                <strong>{i.municipioNome}</strong> <small class="dica">{dataHora(i.criadaEm)} · nota {i.payload.notaAprovacao}</small>
                <small class="sub">{i.pesquisaTitulo}</small>
                {i.estado === 'rejeitada' ? <><Selo texto="Recusada" tom="vermelho" /> <span class="erro">{i.erro}</span></> : <Selo texto="Aguardando envio" tom="ambar" />}
              </div>
              <div class="coleta-item-acoes">
                {i.estado === 'rejeitada' && <button type="button" class="botao" onClick={() => void tentarDeNovo(i.idLocal)}>Tentar de novo</button>}
                <button type="button" class="botao perigo" onClick={() => void descartarItem(i.idLocal, rotulo)}>Descartar</button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
